import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import BezierEasing from 'bezier-easing';
import AbstractAvatar from '@/components/Avatars/AbstractAvatar';
import AdminAvatarMenu from '@/components/Avatars/AdminAvatarMenu';
import LevelBadge from '@/components/Levels/LevelBadge';
import { IJackpotPlayer } from '@/utils/types/IJackpotPlayer';
import { getColor, getLevelColor } from '@/utils/colors';
import { COINFLIP_LIFETIME } from '@/utils/constants';
import { syncedNow, onTimesyncChange } from '@/utils/timesync';
import { playSound } from '@/utils/playSound';

type Phase = 'idle' | 'spinning' | 'revealing' | 'winning';

interface Props {
    gameId: number;
    bank: number;
    users: IJackpotPlayer[];
    minbet: number;
    winnerUser?: IJackpotPlayer;
    winnerBlock: boolean;
    rollAt: number | null;
    revealAt: number | null;
    winnerAngle: number | null;
    clearAt: number | null;
    timer: string;
    isAuthed: boolean;
    onJoin: () => void;
    onLogin: () => void;
    rollAudio: React.MutableRefObject<HTMLAudioElement | null>;
    soundsEnabled: React.MutableRefObject<boolean>;
}

// Ease-out deceleration curve for the spin. A single RAF loop evaluates this
// every frame to drive both the wheel rotation and the audio ticks, so they
// can never drift apart. cubic-bezier(0.10, 0.65, 0.05, 1) is a smooth,
// monotonic ease-out that decelerates hard into the final sector.
const EASING_PARAMS: [number, number, number, number] = [0.10, 0.65, 0.05, 1];
const easing = BezierEasing(...EASING_PARAMS);

const REVEAL_MS = 550;
const TURNS = 8;
const GRADIENT_TWEEN_MS = 600;
const EMPTY_GRADIENT = 'conic-gradient(from -90deg, rgba(255,255,255,0.05) 0% 100%)';

const formatMoney = (v: number) => `$${v.toFixed(2)}`;

const CoinIcon = ({ size = 18 }: { size?: number }) => (
    <span
        className="rounded-full grid place-items-center shrink-0"
        style={{
            width: size,
            height: size,
            background: 'radial-gradient(circle at 35% 30%, #ffe08a, #ffc24d)',
            boxShadow: '0 0 12px rgba(255,194,77,0.7)',
        }}
    >
        <span
            className="font-black leading-none"
            style={{ fontSize: size * 0.6, color: '#6a4500', textShadow: '0 1px 0 rgba(255,255,255,0.45)' }}
        >
            $
        </span>
    </span>
);

interface Slot {
    id: number;
    color: string;
    share: number;
}

// Crisp segment boundaries (like the reference circle's hard conic stops) —
// no colour blending between adjacent slices, so there's no soft/“blurred”
// transition colour on the edges. Set > 0 to re-introduce a smooth blend.
const BLEND_PCT = 0;

function buildGradient(slots: Slot[]): string {
    const visible = slots.filter((s) => s.share > 0.0001);
    if (visible.length === 0) return EMPTY_GRADIENT;
    if (visible.length === 1) {
        // Single segment — no boundaries to worry about, just paint the whole ring.
        return `conic-gradient(from -90deg, ${visible[0].color} 0% 100%)`;
    }
    let acc = 0;
    const stops: string[] = [];
    let total = 0;
    visible.forEach((s) => (total += s.share));
    visible.forEach((s, i) => {
        const portion = (s.share / total) * 100;
        const from = acc;
        const to = acc + portion;
        // Shrink the segment slightly on the side that has a neighbour so the
        // gap becomes a smooth color-interpolated blend. Tiny segments fall
        // back to a single stop to avoid producing inverted ranges.
        const localBlend = Math.min(BLEND_PCT, portion / 3);
        const start = i === 0 ? from : from + localBlend;
        const end = i === visible.length - 1 ? to : to - localBlend;
        if (end > start + 0.01) {
            stops.push(`${s.color} ${start.toFixed(3)}% ${end.toFixed(3)}%`);
        } else {
            stops.push(`${s.color} ${((from + to) / 2).toFixed(3)}%`);
        }
        acc = to;
    });
    return `conic-gradient(from -90deg, ${stops.join(', ')})`;
}

// Minimum visual size every active segment occupies, expressed as a fraction
// of the full circle (%). A real chance of e.g. 1.7% is only ~6° of arc — too
// thin to see on the ring and awkward for the pointer to land on. We give each
// slice a visible floor so a tiny-stake player still gets a clearly landable
// wedge. 5% ≈ 18° of arc, which reads as a solid sliver at the wheel's size.
//
// IMPORTANT: this is purely cosmetic — the winner is decided by the server, so
// inflating a slice does NOT change anyone's real odds. The floor is applied
// identically to the gradient, the landing-angle math, the tick boundaries and
// the hover tooltip, so the pointer always stops on the visual slice it shows.
const MIN_VISUAL_PCT = 5;

// Map raw chance percentages to the shares actually drawn on the wheel: every
// segment gets at least MIN_VISUAL_PCT, the remainder is split proportionally
// to real chance, and the result sums to 100. If there are too many players
// for every floor to fit, it gracefully degrades to an equal split.
function getVisualShares(users: IJackpotPlayer[]): number[] {
    const n = users.length;
    if (n === 0) return [];
    const totalRaw = users.reduce((s, u) => s + u.chancePercent, 0);
    // Floor we can actually afford — never more than an equal slice.
    const minPct = Math.min(MIN_VISUAL_PCT, 100 / n);
    const free = 100 - minPct * n; // proportional budget left after the floors (>= 0)
    if (totalRaw <= 0) return users.map(() => 100 / n);
    return users.map((u) => minPct + (u.chancePercent / totalRaw) * free);
}

const WheelStage = ({
    gameId,
    bank,
    users,
    winnerUser,
    winnerBlock,
    rollAt,
    revealAt,
    winnerAngle,
    clearAt,
    timer,
    isAuthed,
    onJoin,
    onLogin,
    rollAudio,
    soundsEnabled,
}: Props) => {
    const spinnerRef = useRef<HTMLDivElement>(null);
    const amountRef = useRef<HTMLDivElement>(null);
    const rafRef = useRef<number | null>(null);
    const gradRafRef = useRef<number | null>(null);
    const displayedSlotsRef = useRef<Slot[]>([]);
    // Final landing angle + the rotation we used as the round's "start" are
    // cached per gameId so the timesync re-run below uses the round's true
    // starting frame instead of treating the mid-animation current rotation
    // as a new origin (which would add 8 extra turns and miscount audio ticks).
    const finalDegRef = useRef<{ gameId: number; origStart: number; finalDeg: number } | null>(
        null
    );

    const [phase, setPhase] = useState<Phase>('idle');
    const [countdown, setCountdown] = useState<number>(COINFLIP_LIFETIME / 2);
    // Bumped whenever the timesync offset changes — used as a useEffect dep so
    // a mid-roll mount can recompute the spin once the real server clock
    // arrives (otherwise the CSS transition uses Date.now() and can be wildly
    // off, making the wheel look "frozen" on slow/skewed clients).
    const [tsTick, setTsTick] = useState(0);
    useEffect(() => onTimesyncChange(() => setTsTick((n) => n + 1)), []);

    // Tooltip state — shown when the cursor is over a colored ring segment in idle phase.
    const [tooltip, setTooltip] = useState<{
        user: IJackpotPlayer;
        x: number;
        y: number;
    } | null>(null);
    const [mounted, setMounted] = useState(false);
    useEffect(() => {
        setMounted(true);
    }, []);

    function applyGradient(grad: string) {
        // Only the ring carries the segment colours; the glow is a fixed
        // violet→cyan box-shadow on the ring (.jp-wheel-spinner), so it never
        // duplicates the slice colours inside the circle.
        if (spinnerRef.current) spinnerRef.current.style.background = grad;
    }

    // Smoothly tween conic-gradient when users array changes
    useEffect(() => {
        // Color is provided by the backend (IJackpotPlayer.color → getColor()), stable per user.
        // Use visual shares (with the per-segment minimum) so tiny-chance
        // players still render a visible slice.
        const vShares = getVisualShares(users);
        const target: Slot[] = users.map((u, i) => ({
            id: u.id,
            color: getColor(u.color) || 'rgba(255,255,255,0.10)',
            share: vShares[i],
        }));

        // Union of ids (target first to preserve order, then leftovers from "from")
        const from = displayedSlotsRef.current;
        const fromById = new Map(from.map((s) => [s.id, s]));
        const targetIds = new Set(target.map((s) => s.id));
        const merged: Slot[] = target.map((s) => ({
            id: s.id,
            color: s.color,
            share: fromById.get(s.id)?.share ?? 0, // start share
        }));
        // Departing slots — shrink to 0
        from.forEach((s) => {
            if (!targetIds.has(s.id)) merged.push({ id: s.id, color: s.color, share: s.share });
        });

        const startShares = merged.map((s) => s.share);
        const endShares = merged.map((s) => {
            const t = target.find((x) => x.id === s.id);
            return t ? t.share : 0;
        });

        if (gradRafRef.current) cancelAnimationFrame(gradRafRef.current);

        const t0 = performance.now();
        const step = (now: number) => {
            const t = Math.min(1, (now - t0) / GRADIENT_TWEEN_MS);
            const e = 1 - Math.pow(1 - t, 3); // ease-out cubic
            const frame: Slot[] = merged.map((s, i) => ({
                id: s.id,
                color: s.color,
                share: startShares[i] + (endShares[i] - startShares[i]) * e,
            }));
            applyGradient(buildGradient(frame));
            if (t < 1) {
                gradRafRef.current = requestAnimationFrame(step);
            } else {
                // Drop fully-shrunk slots from the displayed list
                displayedSlotsRef.current = frame.filter((s) => s.share > 0.0001);
                gradRafRef.current = null;
            }
        };
        gradRafRef.current = requestAnimationFrame(step);

        return () => {
            if (gradRafRef.current) cancelAnimationFrame(gradRafRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [users.map((u) => `${u.id}:${u.chancePercent}`).join('|')]);

    // Count how many times the pointer (at 0°) crossed any of the given
    // boundary rotations as absolute rotation went from prev → curr (forward).
    function countCrossings(prev: number, curr: number, boundaries: number[]): number {
        if (curr <= prev) return 0;
        let count = 0;
        for (const b of boundaries) {
            // The boundary is hit at every absolute rotation b + 360k
            const firstK = Math.floor((prev - b) / 360) + 1;
            const lastK = Math.floor((curr - b) / 360);
            if (lastK >= firstK) count += lastK - firstK + 1;
        }
        return count;
    }

    function getRotationDeg(el: HTMLElement): number {
        const t = getComputedStyle(el).transform;
        if (!t || t === 'none') return 0;
        try {
            const m = new DOMMatrix(t);
            return (Math.atan2(m.b, m.a) * 180) / Math.PI;
        } catch {
            return 0;
        }
    }

    function animateAmount(to: number, dur = 1400) {
        const el = amountRef.current;
        if (!el) return;
        const start = performance.now();
        const from = 0;
        const step = (now: number) => {
            const t = Math.min(1, (now - start) / dur);
            const e = 1 - Math.pow(1 - t, 3);
            el.textContent = formatMoney(from + (to - from) * e);
            if (t < 1) rafRef.current = requestAnimationFrame(step);
        };
        rafRef.current = requestAnimationFrame(step);
    }

    // Spin animation — driven by server-provided rollAt / revealAt / winnerUser.
    //
    // bandit.camp-style engine: the rotation is driven entirely by
    // requestAnimationFrame (NOT a CSS transition). Every frame we write an
    // inline `transform: rotate(Xdeg)`, where X is an absolutely-accumulated
    // angle (it never wraps back into 0–360 between rounds). The deceleration
    // is an ease-out curve, and the SAME RAF loop both rotates the wheel and
    // emits the boundary-crossing audio ticks, so visual and sound can never
    // drift apart.
    //
    // Note: we compute the landing angle from winnerUser + the same continuous
    // chance%-segment layout the wheel renders, so the pointer always stops on
    // the winner's color slice. The backend's winnerAngle uses a different
    // 18-slot grid and isn't directly compatible with the frontend wheel —
    // since all clients receive the same `users` array (same chance%, same
    // colors), this client-side calc is also synced across clients.
    useEffect(() => {
        if (rollAt == null || revealAt == null) return;
        if (!winnerUser || users.length === 0) return;
        const spinner = spinnerRef.current;
        if (!spinner) return;

        const winnerIdx = users.findIndex((u) => u.id === winnerUser.id);
        if (winnerIdx < 0) return;

        const totalSpinMs = revealAt - rollAt;
        if (totalSpinMs <= 0) return;

        // Cumulative share before the winner, plus half their share = center
        // percentage. Computed from VISUAL shares (with the min-size floor), so
        // the pointer lands on the slice exactly as it's drawn on the ring.
        const vShares = getVisualShares(users);
        const totalShare = vShares.reduce((s, v) => s + v, 0) || 100;
        let accBefore = 0;
        for (let i = 0; i < winnerIdx; i++) accBefore += vShares[i];
        const winnerShare = vShares[winnerIdx];
        const centerPct = ((accBefore + winnerShare / 2) / totalShare) * 100;
        // Convention (same as buildGradient + hover tooltip):
        //   gradient `from -90deg` → percent P sits at CSS angle (-90 + P*3.6)
        //   pointer is at 0° → rotation R that brings center under pointer:
        //   R = 90 - P*3.6   (mod 360)
        const centerDeg = (centerPct / 100) * 360;
        const targetMod = ((90 - centerDeg) % 360 + 360) % 360;

        // Optional deterministic jitter within the winner's slice — derived
        // from winnerAngle (which itself is deterministic from the secret) so
        // the angle is varied but identical on every client.
        const segDeg = (winnerShare / totalShare) * 360;
        const maxJitter = Math.min(segDeg * 0.6, 14);
        const jitterSource = winnerAngle != null ? winnerAngle : 0;
        const normalizedJitter = ((jitterSource / 360) - 0.5) * 2; // [-1, 1)
        const jitter = normalizedJitter * (maxJitter / 2);

        const currentDeg = getRotationDeg(spinner);
        const currentMod = ((currentDeg % 360) + 360) % 360;

        // Cache final landing angle + this round's true start rotation so the
        // effect can re-run after timesync without piling on extra turns.
        //
        // The angle accumulates ABSOLUTELY (bandit.camp behaviour): finalDeg is
        // the round's starting rotation + N full turns + the offset that brings
        // the winner's slice under the pointer. It is never wrapped into
        // 0–360, so each round simply keeps counting up from where the last one
        // stopped.
        let origStart: number;
        let finalDeg: number;
        if (finalDegRef.current?.gameId === gameId) {
            origStart = finalDegRef.current.origStart;
            finalDeg = finalDegRef.current.finalDeg;
        } else {
            const delta = ((targetMod + jitter - currentMod + 720) % 360);
            origStart = currentDeg;
            finalDeg = currentDeg + TURNS * 360 + delta;
            finalDegRef.current = { gameId, origStart, finalDeg };
        }

        // No CSS transition — the RAF loop owns the transform every frame.
        spinner.style.transition = 'none';

        // Compute color boundary angles in absolute-rotation space.
        // Same convention as buildGradient / hover tooltip:
        //   percent P (0..100) corresponds to CSS angle (P*3.6 - 90)
        //   pointer is at 0° → boundary B is crossed when rotation ≡ (90 - B*3.6) (mod 360)
        // Boundary percentages (between segments). Drop trivial < 0.0001 to avoid
        // doubled ticks at the joints of zero-share neighbours.
        const boundaryPercents: number[] = [];
        let acc = 0;
        for (let i = 0; i < users.length; i++) {
            acc += (vShares[i] / totalShare) * 100;
            if (i < users.length - 1) boundaryPercents.push(acc);
        }
        const boundaryRotations = boundaryPercents.map(
            (p) => ((90 - p * 3.6) % 360 + 360) % 360
        );

        // Position for an arbitrary synced timestamp using the ease-out curve.
        // progress 0→1 maps to origStart→finalDeg; easing decelerates to the end.
        // Works for mid-roll joins too: progress is derived from the wall clock,
        // so a late mount snaps straight to the correct in-flight angle and
        // continues from there — no separate "catch-up" branch needed.
        const rotationAt = (t: number) => {
            const progress = Math.min(1, Math.max(0, (t - rollAt) / totalSpinMs));
            return origStart + (finalDeg - origStart) * easing(progress);
        };

        // Paint the very first frame synchronously so there's no flash at the
        // origin before RAF kicks in.
        const startNow = syncedNow();
        let lastRot = rotationAt(startNow);
        spinner.style.transform = `rotate(${lastRot}deg) translateZ(0)`;

        if (startNow >= revealAt) {
            // Already past revealAt (joined after the roll) — settle instantly.
            spinner.style.transform = `rotate(${finalDeg}deg) translateZ(0)`;
            setPhase('revealing');
            return;
        }

        setPhase('spinning');

        // Single RAF loop: drives the rotation AND emits a tick for every
        // segment boundary that passed under the pointer since the last frame.
        let rafId = 0;
        let cancelled = false;
        const tick = () => {
            if (cancelled) return;
            const t = syncedNow();
            if (t >= revealAt) {
                // Final frame — land exactly on finalDeg and count any
                // boundaries crossed on the way in.
                const crossed = countCrossings(lastRot, finalDeg, boundaryRotations);
                for (let i = 0; i < crossed; i++) {
                    playSound(rollAudio, soundsEnabled.current);
                }
                spinner.style.transform = `rotate(${finalDeg}deg) translateZ(0)`;
                setPhase('revealing');
                return;
            }
            const currRot = rotationAt(t);
            spinner.style.transform = `rotate(${currRot}deg) translateZ(0)`;
            const crossed = countCrossings(lastRot, currRot, boundaryRotations);
            for (let i = 0; i < crossed; i++) {
                playSound(rollAudio, soundsEnabled.current);
            }
            lastRot = currRot;
            rafId = requestAnimationFrame(tick);
        };
        rafId = requestAnimationFrame(tick);

        return () => {
            cancelled = true;
            cancelAnimationFrame(rafId);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rollAt, revealAt, winnerUser?.id, gameId, tsTick]);

    // Phase: revealing → winning (after winnerBlock prop becomes true)
    useEffect(() => {
        if (phase !== 'revealing') return;
        const t = window.setTimeout(() => {
            if (winnerBlock) setPhase('winning');
        }, REVEAL_MS);
        return () => window.clearTimeout(t);
    }, [phase, winnerBlock]);

    useEffect(() => {
        if (winnerBlock && phase === 'revealing') {
            setPhase('winning');
        }
        if (!winnerBlock && phase === 'winning') {
            setPhase('idle');
            const spinner = spinnerRef.current;
            if (spinner) {
                const now = getRotationDeg(spinner);
                spinner.style.transition = 'none';
                spinner.style.transform = `rotate(${now}deg) translateZ(0)`;
            }
        }
    }, [winnerBlock, phase]);

    // When entering winning: animate amount + synced countdown to clearAt
    useEffect(() => {
        if (phase !== 'winning') return;
        const t = window.setTimeout(() => animateAmount(bank), 560);

        const updateCountdown = () => {
            if (clearAt == null) {
                setCountdown(0);
                return;
            }
            const left = Math.max(0, clearAt - syncedNow());
            setCountdown(Math.ceil(left / 1000));
        };
        updateCountdown();
        const iv = window.setInterval(updateCountdown, 200);

        return () => {
            window.clearTimeout(t);
            window.clearInterval(iv);
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, [phase, bank, clearAt]);

    // Hover-tooltip over the colored ring. Maps cursor position → segment → player.
    useEffect(() => {
        const el = spinnerRef.current;
        if (!el) return;
        if (phase !== 'idle' || users.length === 0) {
            if (tooltip) setTooltip(null);
            return;
        }
        // `inset` of the ::after hole — keep in sync with .jp-wheel-spinner::after { inset: 3.7% }
        const RING_INSET = 0.037;

        function findUserAt(clientX: number, clientY: number): IJackpotPlayer | null {
            if (!el) return null;
            const rect = el.getBoundingClientRect();
            const cx = rect.left + rect.width / 2;
            const cy = rect.top + rect.height / 2;
            const dx = clientX - cx;
            const dy = clientY - cy;
            const r = Math.hypot(dx, dy);
            const outer = rect.width / 2;
            const inner = outer - rect.width * RING_INSET;
            // Cursor must be inside the colored ring, not over the dark hole
            if (r < inner || r > outer) return null;

            // Cursor angle measured CW from 12 o'clock (CSS convention)
            const cssAngle = ((Math.atan2(dy, dx) * 180) / Math.PI + 90 + 360) % 360;
            // Subtract current spinner rotation so the math is in the unrotated gradient frame
            const rotation = getRotationDeg(el);
            const local = ((cssAngle - rotation) % 360 + 360) % 360;
            // Gradient is `from -90deg` (0% sits at 9 o'clock = 270° CSS), so
            // percent = (localAngle + 90) / 3.6 mod 100
            const percent = ((local + 90) % 360) / 3.6;

            // Match the drawn slices: use visual shares (with the min floor).
            const vShares = getVisualShares(users);
            const totalShare = vShares.reduce((s, v) => s + v, 0) || 100;
            let acc = 0;
            for (let i = 0; i < users.length; i++) {
                acc += (vShares[i] / totalShare) * 100;
                if (percent <= acc + 0.0001) return users[i];
            }
            return null;
        }

        function onMove(e: MouseEvent) {
            const u = findUserAt(e.clientX, e.clientY);
            if (!u) {
                setTooltip((prev) => (prev ? null : prev));
                return;
            }
            setTooltip({ user: u, x: e.clientX, y: e.clientY });
        }

        function onLeave() {
            setTooltip((prev) => (prev ? null : prev));
        }

        el.addEventListener('mousemove', onMove);
        el.addEventListener('mouseleave', onLeave);
        return () => {
            el.removeEventListener('mousemove', onMove);
            el.removeEventListener('mouseleave', onLeave);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phase, users.map((u) => `${u.id}:${u.chancePercent}`).join('|')]);

    // Reset phase on game id change.
    // BUT skip when we're mounting mid-roll (rollAt/revealAt/winnerUser set).
    // This effect is declared after the spin-setup effect, so without the
    // skip it overwrites setPhase('spinning') with 'idle' in the same render
    // and the wheel never starts spinning after a mid-roll navigation.
    useEffect(() => {
        if (rollAt != null && revealAt != null && winnerUser) return;
        setPhase('idle');
        const spinner = spinnerRef.current;
        if (spinner) {
            spinner.style.transition = 'none';
            spinner.style.transform = 'rotate(0deg) translateZ(0)';
        }
        // Clear cached slots and reset to empty gradient for the new round
        displayedSlotsRef.current = [];
        applyGradient(EMPTY_GRADIENT);
        // Also drop the cached final-angle for this gameId so the next round
        // recomputes from a clean slate.
        finalDegRef.current = null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gameId]);

    const innerHidden = phase === 'revealing' || phase === 'winning';
    const winnerVisible = phase === 'winning' && winnerUser;

    // Center timer display (mirrors the reference circle: "Rolling in" + big
    // seconds + the pot). `timer` arrives as "00:SS" while counting, or as
    // "ROLL" / "Waiting for users" in the other phases.
    const timeMatch = timer.match(/^\d{1,2}:(\d{2})$/);
    const counting = !!timeMatch;
    const bigTimer = counting ? timeMatch![1] : timer;
    const longTimer = bigTimer.length > 3;
    const timerLabel = counting ? 'Rolling in' : timer === 'ROLL' ? 'Selecting winner' : 'Jackpot';

    return (
        <div className="relative w-full flex flex-col items-center gap-6 overflow-visible">
                <div className="relative w-[min(570px,82vw)] aspect-square">
                    {/* spinner */}
                    <div
                        ref={spinnerRef}
                        className="jp-wheel-spinner absolute inset-0 rounded-full z-[1]"
                    />

                    {/* ticks */}
                    <div className="jp-wheel-ticks" />

                    {/* pointer */}
                    <div className="absolute left-1/2 -translate-x-1/2 -top-2 z-[4]">
                        <div
                            className="w-0 h-0"
                            style={{
                                borderLeft: '13px solid transparent',
                                borderRight: '13px solid transparent',
                                borderTop: '18px solid #46e08a',
                                filter: 'drop-shadow(0 2px 12px rgba(70,224,138,0.75))',
                            }}
                        />
                        <div
                            className="absolute left-1/2 -translate-x-1/2 -top-[22px] w-3.5 h-2 rounded-t bg-accent_4"
                            style={{ boxShadow: '0 0 12px rgba(70,224,138,0.65)' }}
                        />
                    </div>

                    {/* inner */}
                    <div
                        className={`absolute inset-[11.4%] rounded-full border border-stroke flex flex-col items-center justify-center gap-1.5 z-[3] transition-all duration-300 ${
                            innerHidden ? 'opacity-0 scale-75 pointer-events-none' : ''
                        }`}
                        style={{
                            background:
                                'radial-gradient(circle at 50% 40%, #1a1338, #0a0718 75%)',
                            boxShadow:
                                'inset 0 0 50px rgba(120,80,255,0.25), inset 0 1px 0 rgba(255,255,255,0.06), 0 12px 32px rgba(0,0,0,0.5)',
                        }}
                    >
                        <div className="text-[13px] max-md:text-[10px] text-text_3 uppercase tracking-[0.24em]">
                            {timerLabel}
                        </div>
                        <div
                            className={`font-bold tracking-[-0.02em] text-text_main tabular-nums leading-none ${
                                longTimer
                                    ? 'text-3xl max-md:text-lg text-center px-4'
                                    : 'text-[128px] max-md:text-[52px]'
                            }`}
                            style={{ textShadow: '0 0 30px rgba(58,214,255,0.7), 0 0 70px rgba(139,109,255,0.5)' }}
                        >
                            {bigTimer}
                        </div>
                        <div
                            className="mt-2 flex items-center gap-2 text-[27px] max-md:text-base font-extrabold text-accent_5 tabular-nums"
                            style={{ textShadow: '0 0 16px rgba(255,194,77,0.6)' }}
                        >
                            <CoinIcon size={22} />
                            {bank.toFixed(2)}
                        </div>
                    </div>

                    {/* winner reveal — rotating rays + soft spotlight behind
                        the avatar/amount (replaces the old expanding rings) */}
                    {winnerVisible && (
                        <>
                            <div className="jp-wheel-rays" />
                            <div className="jp-wheel-winner-glow" />
                        </>
                    )}

                    {/* winner overlay */}
                    {winnerUser && (
                        <div
                            className={`absolute inset-[11.4%] rounded-full z-[3] flex flex-col items-center justify-center text-center px-6 max-md:px-3 py-5 max-md:py-3 transition-all ${
                                winnerVisible
                                    ? 'opacity-100 scale-100 pointer-events-auto'
                                    : 'opacity-0 scale-75 pointer-events-none'
                            }`}
                            style={{ transitionDuration: '0.55s' }}
                        >
                            <div
                                className="mb-2"
                                style={{
                                    boxShadow:
                                        '0 0 0 3px rgba(255,194,77,0.9), 0 0 26px rgba(255,194,77,0.6)',
                                    borderRadius: '28%',
                                    animation: winnerVisible
                                        ? 'wnAvaPop 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) 0.05s both'
                                        : undefined,
                                    opacity: winnerVisible ? undefined : 0,
                                }}
                            >
                                <AdminAvatarMenu
                                    userId={winnerUser.id}
                                    userName={winnerUser.name}
                                    gameType="jackpot"
                                    gameId={gameId}
                                >
                                    <AbstractAvatar
                                        seed={winnerUser.name || String(winnerUser.id)}
                                        src={winnerUser.avatar}
                                        size={92}
                                        square
                                        className="max-md:!w-12 max-md:!h-12"
                                    />
                                </AdminAvatarMenu>
                            </div>
                            <div
                                className="text-[22px] max-md:text-sm font-extrabold tracking-[0.01em] truncate max-w-[280px] text-white"
                                style={{
                                    textShadow: '0 0 18px rgba(255,194,77,0.45)',
                                    animation: winnerVisible
                                        ? 'fluentFadeUp 0.45s ease-out 0.35s both'
                                        : undefined,
                                    opacity: winnerVisible ? undefined : 0,
                                }}
                            >
                                {winnerUser.name}
                            </div>
                            <div
                                className="text-[11px] tracking-[0.3em] uppercase font-bold mt-2"
                                style={{
                                    color: '#ffc24d',
                                    textShadow: '0 0 12px rgba(255,194,77,0.55)',
                                    animation: winnerVisible
                                        ? 'fluentFadeUp 0.4s ease-out 0.5s both'
                                        : undefined,
                                    opacity: winnerVisible ? undefined : 0,
                                }}
                            >
                                Won
                            </div>
                            <div
                                ref={amountRef}
                                className="text-[58px] max-md:text-[30px] font-extrabold tracking-[-0.02em] tabular-nums leading-none mt-1"
                                style={{
                                    color: '#ffd66b',
                                    textShadow:
                                        '0 0 14px rgba(255,214,107,0.85), 0 0 36px rgba(255,194,77,0.55)',
                                    animation: winnerVisible
                                        ? 'wnAmountPop 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) 0.55s both'
                                        : undefined,
                                    opacity: winnerVisible ? undefined : 0,
                                }}
                            >
                                $0.00
                            </div>
                            <div
                                className="inline-flex items-center gap-2 mt-2.5 px-3 py-1 rounded-full text-[11px] font-semibold"
                                style={{
                                    color: '#ffe0a3',
                                    background: 'rgba(255,194,77,0.10)',
                                    border: '1px solid rgba(255,194,77,0.35)',
                                    boxShadow: '0 0 14px -4px rgba(255,194,77,0.6)',
                                    animation: winnerVisible
                                        ? 'fluentFadeUp 0.4s ease-out 0.8s both'
                                        : undefined,
                                    opacity: winnerVisible ? undefined : 0,
                                }}
                            >
                                Chance{' '}
                                <b className="text-white font-extrabold">
                                    {winnerUser.chancePercent.toFixed(2)}%
                                </b>
                            </div>
                            <div
                                className="mt-2.5 text-[11px] text-text_3 tracking-[0.04em] inline-flex items-center gap-1.5"
                                style={{
                                    animation: winnerVisible
                                        ? 'fluentFadeUp 0.4s ease-out 1s both'
                                        : undefined,
                                    opacity: winnerVisible ? undefined : 0,
                                }}
                            >
                                Next roll in
                                <b className="text-text_main font-bold tabular-nums px-2 py-px bg-layer_3 border border-stroke rounded min-w-6 text-center">
                                    {countdown}
                                </b>
                                s
                            </div>
                        </div>
                    )}
                </div>

            {/* Join / Login CTA — sits below the circle (reference keeps the
                circle centre clean: label + timer + pot only). */}
            {phase !== 'winning' && (
                <button
                    onClick={isAuthed ? onJoin : onLogin}
                    className="inline-flex items-center justify-center gap-2 px-8 max-md:px-6 py-3 max-md:py-2.5 rounded-full border text-white font-bold text-[14px] max-md:text-[12px] tracking-wide transition-transform hover:-translate-y-0.5 hover:brightness-[1.06]"
                    style={
                        isAuthed
                            ? {
                                  background: 'linear-gradient(180deg, #4ddcff, #1aa8d8)',
                                  color: '#04131c',
                                  borderColor: 'rgba(255,255,255,0.16)',
                                  boxShadow:
                                      '0 0 22px rgba(58,214,255,0.55), 0 6px 20px rgba(58,214,255,0.35), inset 0 1px 0 rgba(255,255,255,0.4)',
                              }
                            : {
                                  background: 'linear-gradient(180deg, #5cea9d, #2bb76b)',
                                  color: '#04150c',
                                  borderColor: 'rgba(255,255,255,0.16)',
                                  boxShadow:
                                      '0 0 22px rgba(70,224,138,0.55), 0 6px 20px rgba(70,224,138,0.4), inset 0 1px 0 rgba(255,255,255,0.4)',
                              }
                    }
                >
                    {isAuthed ? (
                        '+ Join Game'
                    ) : (
                        <>
                            <img
                                src="/icons/steam.svg"
                                alt=""
                                width={16}
                                height={16}
                                className="opacity-90"
                            />
                            LOGIN
                        </>
                    )}
                </button>
            )}

            {mounted &&
                tooltip &&
                createPortal(
                    <WheelTooltip user={tooltip.user} x={tooltip.x} y={tooltip.y} />,
                    document.body
                )}
        </div>
    );
};

function WheelTooltip({
    user,
    x,
    y,
}: {
    user: IJackpotPlayer;
    x: number;
    y: number;
}) {
    const color = getColor(user.color) || 'rgba(255,255,255,0.10)';
    const lvl = Math.max(0, Math.floor((user.experience ?? 0) / 100));
    const W = 280;
    // Position above the cursor, then clamp to viewport
    let left = x - W / 2;
    let top = y - 18;
    if (typeof window !== 'undefined') {
        left = Math.max(8, Math.min(window.innerWidth - W - 8, left));
        if (top < 8) top = y + 22;
    }
    return (
        <div
            className="fixed pointer-events-none z-[1000] flex items-center gap-3 p-3 rounded-fl-md border border-stroke_strong"
            style={{
                left,
                top,
                width: W,
                transform: 'translateY(-100%)',
                background: 'rgba(18,13,38,0.94)',
                backdropFilter: 'blur(20px) saturate(140%)',
                WebkitBackdropFilter: 'blur(20px) saturate(140%)',
                boxShadow: `0 0 26px -4px ${color}, 0 16px 40px -16px rgba(0,0,0,0.9)`,
                borderColor: color,
            }}
        >
            {/* Avatar with colored ring matching the player's segment */}
            <div className="relative w-12 h-12 shrink-0">
                <AbstractAvatar
                    seed={user.name || String(user.id)}
                    src={user.avatar}
                    size={48}
                    square
                    className="!w-full !h-full"
                />
                <span
                    className="absolute -inset-[2px] rounded-[28%] pointer-events-none"
                    style={{
                        border: `2px solid ${getLevelColor(lvl)}`,
                        boxShadow: `0 0 12px -2px ${getLevelColor(lvl)}`,
                    }}
                />
            </div>

            <div className="flex flex-col gap-1 min-w-0 flex-1">
                <div className="flex items-center gap-1.5 min-w-0">
                    <span className="text-text_main text-[14px] font-semibold truncate flex-1 min-w-0">
                        {user.name}
                    </span>
                    <LevelBadge level={lvl} color={getLevelColor(lvl)} size="sm" variant="solid" />
                </div>
                <div className="flex items-baseline gap-2 text-[11px] tabular-nums whitespace-nowrap">
                    <span
                        className="font-bold"
                        style={{ color, textShadow: `0 0 10px ${color}` }}
                    >
                        {user.chancePercent.toFixed(2)}%
                    </span>
                    {user.valueUsd ? (
                        <>
                            <span className="text-text_4">·</span>
                            <span className="text-accent_5 font-bold">
                                ${user.valueUsd.toFixed(2)}
                            </span>
                        </>
                    ) : null}
                </div>
            </div>
        </div>
    );
}

export default WheelStage;
